import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import RichText from "../components/RichText";
import Footnotes from "../components/Footnotes";
import MetadataModal from "../components/MetadataModal";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ScrollToTopButton from "../components/ScrollToTopButton";
import { getArticle } from "../api/articlesApi";
import { API_ORIGIN } from "../api/authApi";
import { localizeTitle } from "../utils/textDirection";

// Section A: public. One page per homepage "مقاله‌های منتخب" card
// (/articles/:id), opened by clicking a card on HomePage.js. Mirrors
// IssueTopicDetailPage.js.
//
// Bilingual by design: title_fa/title_en etc. live together on the same
// row (see domain.Article on the backend) — activeLang just picks which
// half of that single page is currently shown, with the LanguageSwitcher
// toggling it in place rather than navigating anywhere.
function ArticleDetailPage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeLang, setActiveLang] = useState("fa");

  useEffect(() => {
    getArticle(id)
      .then((a) => {
        setArticle(a);
        setActiveLang(a.title_fa ? "fa" : "en");
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="site">
        <SiteHeader />
        <main className="pub-not-found">
          <p>در حال بارگذاری...</p>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="site">
        <SiteHeader />
        <main className="pub-not-found">
          <p>این مقاله یافت نشد.</p>
          <Link to="/" className="back-link">
            ← بازگشت به صفحهٔ اصلی
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const hasFa = Boolean(article.title_fa);
  const hasEn = Boolean(article.title_en);
  const title = (activeLang === "en" ? article.title_en : article.title_fa) || article.title_fa || article.title_en;
  const body = (activeLang === "en" ? article.body_en : article.body_fa) || article.body_fa || article.body_en;
  const footnotes = activeLang === "en" ? article.footnotes_en : article.footnotes_fa;

  const { dir: titleDir, text: titleText } = localizeTitle(title);
  const pageDir = activeLang === "en" ? "ltr" : "rtl";

  return (
    <div className="site">
      <SiteHeader />

      <main className="topic-detail" dir={pageDir} lang={activeLang}>
        <span className="corner corner-tr" />
        <span className="corner corner-tl" />
        <span className="corner corner-br" />
        <span className="corner corner-bl" />

        <Link to="/" className="back-link">
          ← بازگشت به صفحهٔ اصلی
        </Link>

        {article.image_path && (
          <div className="topic-detail-image-frame">
            <img className="topic-detail-image" src={`${API_ORIGIN}${article.image_path}`} alt={title} />
          </div>
        )}

        <header className="topic-detail-header">
          <div className="topic-detail-kicker">
            <span className="topic-detail-kicker-seg" />
            <span className="topic-detail-kicker-label">مقالهٔ منتخب</span>
            <span className="topic-detail-kicker-seg" />
          </div>

          <h1 className="topic-detail-title" dir={titleDir}>{titleText}</h1>
        </header>

        <div className="content-metadata-row">
          <MetadataModal metadata={article.metadata} />
          <LanguageSwitcher activeLang={activeLang} onChange={setActiveLang} hasFa={hasFa} hasEn={hasEn} />
        </div>

        {body ? (
          <RichText className="topic-detail-body" html={body} />
        ) : (
          <p className="topic-detail-body topic-detail-body--empty">هنوز متنی برای این مقاله ثبت نشده است.</p>
        )}

        <Footnotes footnotes={footnotes} />

        <div className="topic-detail-end-mark" aria-hidden="true">
          ❖
        </div>
      </main>

      <SiteFooter />
      <ScrollToTopButton />
    </div>
  );
}

export default ArticleDetailPage;
